import type { ReactNode } from "react";
import { FormFieldLabel } from "./FormFieldLabel";
import { PhoneInput } from "./PhoneInput";
import { GRADE_OPTIONS, formatBirthdayLabel, gradeLabel, type School } from "../domain/data";
import {
  GENDERS,
  HELP_INTERESTS,
  RACES,
  RELATIONSHIPS,
  SCHOOL_FORM_LABELS,
  SCHOOL_FORM_ORDER,
  type AgreeChoice,
  type GenderId,
  type HelpInterestId,
  type PermissionAnswers,
  type RaceId,
  type RelationshipId,
  type YesNo,
} from "../domain/permissionForm";
import { useLocale } from "../state/i18n";

type Locale = "en" | "es";

export const REGISTRATION_STEPS = ["student", "pickup", "health", "policies", "review"] as const;
export type RegistrationStep = (typeof REGISTRATION_STEPS)[number];

/** Maps a validation field id back to the step that collects it. */
export function registrationStepForField(fieldId: string): RegistrationStep {
  if (fieldId.startsWith("student-pickup") || fieldId.startsWith("student-walk")) return "pickup";
  if (
    fieldId.startsWith("student-") ||
    fieldId.startsWith("guardian-") ||
    fieldId.startsWith("additional-")
  ) {
    return "student";
  }
  if (fieldId.startsWith("emergency") || fieldId.startsWith("health-")) return "health";
  if (fieldId.startsWith("policy-") || fieldId.startsWith("liability-")) return "policies";
  return "review";
}

const prose = {
  en: {
    funding:
      "LIFT is part of a 501(c)(3) nonprofit funded through private, state, and federal grants. Collecting demographic information helps us secure funding and keep LIFT a no-cost service for Leelanau County students and families.",
    conduct: [
      "At LIFT, we believe every student deserves to feel safe, supported, and respected. All participants are expected to follow the LIFT Code of Conduct.",
      "L — Lead with Respect: Treat peers, staff, and volunteers with kindness. Use respectful language and behavior.",
      "I — Invest in the Experience: Participate and contribute to a positive group environment.",
      "F — Follow the Rules: Respect people, spaces, and materials. No drugs, alcohol, vapes, weapons, bullying, or unsafe behavior.",
      "T — Take Responsibility: Resolve conflict respectfully and ask for help when needed.",
    ],
    behavior: [
      "LIFT uses a three-step, restorative behavior response process:",
      "Strike 1: Verbal warning and parent notification.",
      "Strike 2: Parent notification and student behavior support plan.",
      "Strike 3: Parent meeting and suspension from programming for a period determined by LIFT leadership.",
      "Zero-tolerance behaviors that threaten safety result in an immediate 2 week pause from programming, a required parent meeting, and additional steps.",
    ],
    dismissal: [
      "Students may only leave LIFT programming with individuals listed on this form, unless prior written permission is provided by a parent or guardian.",
      "Students who leave programming without permission may receive a behavioral strike.",
      "Do you agree to the LIFT dismissal policies listed above?",
    ],
    media:
      "Media Release: I understand that LIFT may take photographs or videos of my student. I consent to LIFT's use of my student's image, voice, name, and story in the organization's digital and print promotional, fundraising, educational, and other communications, without approval or payment. I grant the organization rights to these materials and waive related claims regarding copyright, publicity, privacy, or alterations.",
    participate: [
      "I, the undersigned parent or legal guardian of the above-named participant, grant permission for my child to participate in programs, activities, and events organized, sponsored, or facilitated by LIFT Teen Center. These activities may include, but are not limited to:",
      "Off-site recreational outings, educational field trips, volunteer or service opportunities, visits to farms, petting zoos, or facilities where animals may be present, community events and outings, and recreational, athletic, or social activities.",
      "I understand that activities may occur both on-site and off-site, and may take place at a variety of locations within the community or surrounding areas.",
    ],
    transport: [
      "I authorize LIFT Teen Center staff members, volunteers, or other authorized drivers to transport my child to and from activities associated with the program. Transportation may occur in organization vehicles, rented vehicles, or personal vehicles approved by the organization. Transportation may include travel to:",
      "Recreational venues, community organizations, local businesses, parks or outdoor spaces, and event or activity locations.",
      "I acknowledge that transportation to and from these activities involves inherent risks and I grant permission for my child to participate in such travel.",
    ],
    risks: [
      "I understand that participation in youth programs and activities involves inherent risks that cannot be entirely eliminated. These risks may include, but are not limited to:",
      "Physical injury, accidents occurring during transportation, slips, falls, or other injuries during activities, water-related activities, interacting with animals, and other unforeseen risks related to recreational or community activities.",
      "I knowingly and voluntarily assume all such risks, both known and unknown, associated with my child's participation in activities organized by LIFT Teen Center.",
    ],
    release: [
      "To the fullest extent permitted by law, I hereby release, waive, and discharge LIFT Teen Center, including its directors, officers, employees, volunteers, agents, and partners, from any and all liability, claims, demands, damages, or causes of action that may arise out of or relate to my child's participation in programs, activities, or transportation provided by the organization.",
      "This release includes, but is not limited to, claims for personal injury, property damage, or other losses that may occur during participation in programs, travel to or from activities, or related events.",
    ],
  },
  es: {
    funding:
      "LIFT forma parte de una organización sin fines de lucro 501(c)(3), financiada con subvenciones privadas, estatales y federales. Recopilar información demográfica nos ayuda a obtener fondos y mantener LIFT como un servicio gratuito para los estudiantes y las familias del condado de Leelanau.",
    conduct: [
      "En LIFT, creemos que cada estudiante merece sentirse seguro, apoyado y respetado. Se espera que todos los participantes sigan el Código de Conducta de LIFT.",
      "L — Lidere con respeto: Trate a compañeros, personal y voluntarios con amabilidad. Use lenguaje y comportamiento respetuosos.",
      "I — Involúcrese en la experiencia: Participe y contribuya a un ambiente de grupo positivo.",
      "F — Siga las reglas: Respete a las personas, los espacios y los materiales. No se permiten drogas, alcohol, vapeadores, armas, intimidación ni conducta insegura.",
      "T — Tome responsabilidad: Resuelva los conflictos con respeto y pida ayuda cuando la necesite.",
    ],
    behavior: [
      "LIFT usa un proceso restaurativo de tres pasos ante la conducta:",
      "Falta 1: Advertencia verbal y aviso al padre o tutor.",
      "Falta 2: Aviso al padre o tutor y plan de apoyo para la conducta del estudiante.",
      "Falta 3: Reunión con el padre o tutor y suspensión de la programación por un período que determine el liderazgo de LIFT.",
      "Las conductas de tolerancia cero que amenazan la seguridad resultan en una pausa inmediata de 2 semanas, una reunión obligatoria con el padre o tutor y pasos adicionales.",
    ],
    dismissal: [
      "Los estudiantes solo pueden salir de la programación de LIFT con las personas indicadas en este formulario, a menos que un padre o tutor dé permiso por escrito con anticipación.",
      "Los estudiantes que salgan de la programación sin permiso pueden recibir una falta de conducta.",
      "¿Está de acuerdo con las políticas de salida de LIFT descritas arriba?",
    ],
    media:
      "Autorización de medios: Entiendo que LIFT puede tomar fotografías o videos de mi estudiante. Autorizo el uso de su imagen, voz, nombre e historia en comunicaciones digitales o impresas de promoción, recaudación de fondos, educación y otras comunicaciones de la organización, sin aprobación ni pago. Otorgo a la organización derechos sobre estos materiales y renuncio a reclamos relacionados con derechos de autor, publicidad, privacidad o modificaciones.",
    participate: [
      "Yo, el padre, la madre o el tutor legal del participante nombrado arriba, doy permiso para que mi hijo participe en programas, actividades y eventos organizados, patrocinados o facilitados por LIFT Teen Center. Estas actividades pueden incluir, entre otras:",
      "Salidas recreativas fuera del sitio, excursiones educativas, oportunidades de voluntariado o servicio, visitas a granjas, zoológicos de contacto o lugares donde pueda haber animales, eventos comunitarios y actividades recreativas, deportivas o sociales.",
      "Entiendo que las actividades pueden ocurrir dentro y fuera del sitio, y en distintos lugares de la comunidad o áreas cercanas.",
    ],
    transport: [
      "Autorizo al personal de LIFT Teen Center, a los voluntarios o a otros conductores autorizados a transportar a mi hijo hacia y desde las actividades del programa. El transporte puede ser en vehículos de la organización, vehículos rentados o vehículos personales aprobados por la organización. El transporte puede incluir viajes a:",
      "Lugares recreativos, organizaciones comunitarias, negocios locales, parques o espacios al aire libre, y lugares de eventos o actividades.",
      "Reconozco que el transporte hacia y desde estas actividades implica riesgos inherentes y doy permiso para que mi hijo participe en esos viajes.",
    ],
    risks: [
      "Entiendo que participar en programas y actividades para jóvenes implica riesgos inherentes que no se pueden eliminar por completo. Estos riesgos pueden incluir, entre otros:",
      "Lesiones físicas, accidentes durante el transporte, resbalones, caídas u otras lesiones durante las actividades, actividades relacionadas con el agua, interacción con animales y otros riesgos imprevistos de actividades recreativas o comunitarias.",
      "Asumo de manera consciente y voluntaria todos esos riesgos, conocidos y desconocidos, relacionados con la participación de mi hijo en actividades organizadas por LIFT Teen Center.",
    ],
    release: [
      "En la medida máxima permitida por la ley, libero, renuncio y eximo de responsabilidad a LIFT Teen Center, incluidos sus directores, funcionarios, empleados, voluntarios, agentes y socios, de toda responsabilidad, reclamo, demanda, daño o causa de acción que surja de la participación de mi hijo en programas, actividades o transporte de la organización, o que se relacione con ella.",
      "Esta exención incluye, entre otros, reclamos por lesiones personales, daños a la propiedad u otras pérdidas que puedan ocurrir durante la participación en programas, los viajes hacia o desde las actividades, o eventos relacionados.",
    ],
  },
} as const;

const ui = {
  en: {
    studentTitle: "Student information",
    parent: "Parent or guardian",
    additional: "Additional parent or guardian",
    additionalHelp:
      "Optional. If you add someone here, their name, phone, and relationship are required.",
    pickupTitle: "Pickup and dismissal",
    healthTitle: "Health and safety",
    emergency1: "Emergency contact 1",
    emergency2: "Emergency contact 2",
    policiesTitle: "Policies and agreements",
    policiesLead:
      "Read each policy in full, then record your response directly below it. Where a meeting is offered, you may request one instead of agreeing.",
    reviewTitle: "Review and submit",
    reviewLead: "Check the details below. You can go back to any step to make a change.",
    responseLabel: "Your response",
    ackLabel: "Acknowledgment",
    conductTitle: "Code of conduct",
    behaviorTitle: "Behavior expectations",
    dismissalTitle: "Dismissal policy",
    mediaTitle: "Media release",
    participateTitle: "Permission to participate",
    transportTitle: "Transportation permission",
    risksTitle: "Assumption of risk",
    releaseTitle: "Liability release",
    first: "First name",
    last: "Last name",
    email: "Email",
    phone: "Phone",
    grade: "Grade",
    birthday: "Date of birth",
    studentPhone: "Student phone number (if applicable)",
    address: "Address",
    gender: "Gender",
    race: "What race(s) or ethnicity/ethnicities best describe your student?",
    school: "My student attends",
    pickup: "My student may leave programming with any of the following individuals (name and relationship)",
    walk: "My student may walk home or to a predetermined location from programming.",
    walkYes: "Yes, my student can walk home from programming",
    walkNo: "No, my student needs to be picked up in a vehicle from programming",
    relationship: "Relationship to participant",
    relationshipStudent: "Relationship to student",
    guardianAddress: "Address (if different from student address)",
    help: "As a parent/guardian of a LIFT participant, I am interested in helping with",
    additionalAddress: "Address",
    behavioral:
      "To ensure we can provide a safe and supportive environment, does your child have any behavioral or emotional needs or accommodations we should be aware of? If so, we may reach out to gather more information. While we strive to be inclusive, there may be instances where we are unable to provide the necessary support. If that is the case, we will communicate with you directly to discuss next steps.",
    specifyYes: "Yes, I will specify below",
    specifyNo: "No, this is not applicable to my child",
    behavioralDetail: "If you answered yes, specify your child's needs.",
    medical:
      "Does your student have any medical conditions (ex: asthma, severe allergies, seizures, diabetes) that may require staff awareness or emergency response, including assistance with medication if needed?",
    medicalDetail: "If you answered yes, specify your child's medical conditions.",
    otc: "Do you authorize LIFT staff to administer prescription and/or over-the-counter medications (ex: Tylenol, ibuprofen, antihistamines), as directed by you or according to labeled instructions?",
    authorizeYes: "Yes, I authorize this",
    authorizeNo: "No, I do not authorize this",
    emergencyCare:
      "In the event of a time-sensitive emergency, do you authorize LIFT to seek immediate medical care for your student and assist your student in administering emergency medication (ex: inhaler, EpiPen) if they are unable to do so independently?",
    physician: "Primary care physician name",
    physicianPhone: "Physician phone",
    hospital: "Preferred hospital (if different than Munson)",
    insurance: "Medical insurance provider",
    policy: "Policy number",
    agree: "Yes, I agree",
    meeting: "No, I don't agree and would like to request a meeting with programming staff to discuss",
    mediaYes: "Yes, I agree",
    mediaNo: "No, I don't agree",
    iAgree: "I agree",
    select: "Select",
    notAnswered: "Not answered",
  },
  es: {
    studentTitle: "Información del estudiante",
    parent: "Padre, madre o tutor",
    additional: "Padre, madre o tutor adicional",
    additionalHelp:
      "Opcional. Si agrega a alguien aquí, se requieren su nombre, teléfono y parentesco.",
    pickupTitle: "Recogida y salida",
    healthTitle: "Salud y seguridad",
    emergency1: "Contacto de emergencia 1",
    emergency2: "Contacto de emergencia 2",
    policiesTitle: "Políticas y acuerdos",
    policiesLead:
      "Lea cada política completa y registre su respuesta justo debajo. Cuando se ofrezca una reunión, puede solicitarla en lugar de estar de acuerdo.",
    reviewTitle: "Revisar y enviar",
    reviewLead: "Revise los datos de abajo. Puede volver a cualquier paso para hacer un cambio.",
    responseLabel: "Su respuesta",
    ackLabel: "Confirmación",
    conductTitle: "Código de conducta",
    behaviorTitle: "Expectativas de conducta",
    dismissalTitle: "Política de salida",
    mediaTitle: "Autorización de medios",
    participateTitle: "Permiso para participar",
    transportTitle: "Permiso de transporte",
    risksTitle: "Asunción de riesgos",
    releaseTitle: "Exención de responsabilidad",
    first: "Nombre",
    last: "Apellido",
    email: "Correo",
    phone: "Teléfono",
    grade: "Grado",
    birthday: "Fecha de nacimiento",
    studentPhone: "Teléfono del estudiante (si aplica)",
    address: "Dirección",
    gender: "Género",
    race: "¿Qué raza o etnias describen mejor a su estudiante?",
    school: "Mi estudiante asiste a",
    pickup: "Mi estudiante puede salir de la programación con estas personas (nombre y parentesco)",
    walk: "Mi estudiante puede caminar a casa o a un lugar acordado al salir de la programación.",
    walkYes: "Sí, mi estudiante puede caminar a casa desde la programación",
    walkNo: "No, mi estudiante necesita que lo recojan en un vehículo",
    relationship: "Parentesco con el participante",
    relationshipStudent: "Parentesco con el estudiante",
    guardianAddress: "Dirección (si es distinta a la del estudiante)",
    help: "Como padre o tutor de un participante de LIFT, me interesa ayudar con",
    additionalAddress: "Dirección",
    behavioral:
      "Para ofrecer un ambiente seguro y de apoyo, ¿su hijo tiene necesidades o adaptaciones conductuales o emocionales que debamos conocer? Si es así, podemos comunicarnos para obtener más información. Buscamos ser inclusivos, pero puede haber casos en los que no podamos brindar el apoyo necesario. Si eso ocurre, hablaremos con usted directamente sobre los siguientes pasos.",
    specifyYes: "Sí, lo especificaré abajo",
    specifyNo: "No, esto no aplica a mi hijo",
    behavioralDetail: "Si respondió que sí, especifique las necesidades de su hijo.",
    medical:
      "¿Su estudiante tiene alguna condición médica (por ejemplo: asma, alergias graves, convulsiones, diabetes) que el personal deba conocer o que pueda requerir una respuesta de emergencia, incluida ayuda con medicamentos si es necesario?",
    medicalDetail: "Si respondió que sí, especifique las condiciones médicas de su hijo.",
    otc: "¿Autoriza al personal de LIFT a administrar medicamentos recetados o de venta libre (por ejemplo: Tylenol, ibuprofeno, antihistamínicos), según sus indicaciones o las instrucciones de la etiqueta?",
    authorizeYes: "Sí, autorizo esto",
    authorizeNo: "No, no autorizo esto",
    emergencyCare:
      "En una emergencia en la que el tiempo es importante, ¿autoriza a LIFT a buscar atención médica inmediata para su estudiante y a ayudarle a administrar un medicamento de emergencia (por ejemplo: inhalador, EpiPen) si no puede hacerlo por su cuenta?",
    physician: "Nombre del médico de cabecera",
    physicianPhone: "Teléfono del médico",
    hospital: "Hospital preferido (si es distinto de Munson)",
    insurance: "Proveedor del seguro médico",
    policy: "Número de póliza",
    agree: "Sí, estoy de acuerdo",
    meeting: "No estoy de acuerdo y quisiera solicitar una reunión con el personal de programación para conversarlo",
    mediaYes: "Sí, estoy de acuerdo",
    mediaNo: "No estoy de acuerdo",
    iAgree: "Estoy de acuerdo",
    select: "Seleccionar",
    notAnswered: "Sin respuesta",
  },
} as const;

const chrome = {
  en: {
    progress: "Registration progress",
    short: {
      student: "Student",
      pickup: "Pickup",
      health: "Health",
      policies: "Policies",
      review: "Review",
    },
    back: "Back",
    continue: "Continue",
    submit: "Submit registration",
    requiredNote: "Required unless marked optional.",
    edit: "Edit",
  },
  es: {
    progress: "Progreso del registro",
    short: {
      student: "Estudiante",
      pickup: "Recogida",
      health: "Salud",
      policies: "Políticas",
      review: "Revisar",
    },
    back: "Atrás",
    continue: "Continuar",
    submit: "Enviar registro",
    requiredNote: "Obligatorio, salvo que se indique opcional.",
    edit: "Editar",
  },
} as const;

export function registrationChrome(locale: Locale) {
  return chrome[locale];
}

export function PermissionFormFields({
  value,
  onChange,
  step,
  errorFieldId = "",
  errorMessage = "",
  onEditStep,
}: {
  value: PermissionAnswers;
  onChange: (next: PermissionAnswers) => void;
  step: RegistrationStep;
  errorFieldId?: string;
  errorMessage?: string;
  onEditStep?: (step: RegistrationStep) => void;
}) {
  const { locale } = useLocale();
  const text = ui[locale];
  const story = prose[locale];

  function setStudent(patch: Partial<PermissionAnswers["student"]>) {
    onChange({ ...value, student: { ...value.student, ...patch } });
  }
  function setGuardian(patch: Partial<PermissionAnswers["guardian"]>) {
    onChange({ ...value, guardian: { ...value.guardian, ...patch } });
  }
  function setAdditional(patch: Partial<PermissionAnswers["additionalGuardian"]>) {
    onChange({
      ...value,
      additionalGuardian: { ...value.additionalGuardian, ...patch },
    });
  }
  function setEmergency(which: "emergency1" | "emergency2", patch: Partial<PermissionAnswers["emergency1"]>) {
    onChange({ ...value, [which]: { ...value[which], ...patch } });
  }
  function setHealth(patch: Partial<PermissionAnswers["health"]>) {
    onChange({ ...value, health: { ...value.health, ...patch } });
  }
  function setPolicies(patch: Partial<PermissionAnswers["policies"]>) {
    onChange({ ...value, policies: { ...value.policies, ...patch } });
  }
  function setLiability(patch: Partial<PermissionAnswers["liability"]>) {
    onChange({ ...value, liability: { ...value.liability, ...patch } });
  }

  function toggleRace(id: RaceId) {
    const races = value.student.races.includes(id)
      ? value.student.races.filter((item) => item !== id)
      : [...value.student.races, id];
    setStudent({ races });
  }
  function toggleHelp(id: HelpInterestId) {
    const help = value.guardian.help.includes(id)
      ? value.guardian.help.filter((item) => item !== id)
      : [...value.guardian.help, id];
    setGuardian({ help });
  }

  function fieldError(id: string): string {
    return errorFieldId === id ? errorMessage : "";
  }

  const stepTitle =
    step === "student"
      ? text.studentTitle
      : step === "pickup"
        ? text.pickupTitle
        : step === "health"
          ? text.healthTitle
          : step === "policies"
            ? text.policiesTitle
            : text.reviewTitle;

  return (
    <div className="permission-form">
      <h2 id="reg-step-title" className="reg-step-title" tabIndex={-1}>
        {stepTitle}
      </h2>
      {step === "policies" ? <p className="reg-step-lead">{text.policiesLead}</p> : null}
      {step === "review" ? <p className="reg-step-lead">{text.reviewLead}</p> : null}

      {step === "student" ? (
        <>
          <div className="grid-2">
            <TextField id="student-first" label={text.first} required value={value.student.firstName} onChange={(firstName) => setStudent({ firstName })} autoComplete="given-name" error={fieldError("student-first")} />
            <TextField id="student-last" label={text.last} required value={value.student.lastName} onChange={(lastName) => setStudent({ lastName })} autoComplete="family-name" error={fieldError("student-last")} />
          </div>
          <TextField id="student-email" label={text.email} required type="email" value={value.student.email} onChange={(email) => setStudent({ email })} autoComplete="email" error={fieldError("student-email")} />
          <div className="grid-2">
            <div className={`field${fieldError("student-grade") ? " field--invalid" : ""}`} id="student-grade">
              <FormFieldLabel htmlFor="student-grade-input" required presentation="asterisk">
                {text.grade}
              </FormFieldLabel>
              <select
                id="student-grade-input"
                value={value.student.grade === "" ? "" : String(value.student.grade)}
                aria-invalid={fieldError("student-grade") ? true : undefined}
                aria-describedby={fieldError("student-grade") ? "student-grade-error" : undefined}
                onChange={(event) => {
                  const next = event.target.value;
                  setStudent({ grade: next === "" ? "" : Number(next) });
                }}
              >
                <option value="">{text.select}</option>
                {GRADE_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <FieldError id="student-grade" message={fieldError("student-grade")} />
            </div>
            <TextField id="student-birthday" label={text.birthday} required type="date" value={value.student.birthday} onChange={(birthday) => setStudent({ birthday })} error={fieldError("student-birthday")} />
          </div>
          <div className={`field field--half${fieldError("student-phone") ? " field--invalid" : ""}`}>
            <FormFieldLabel htmlFor="student-phone" optional>
              {text.studentPhone}
            </FormFieldLabel>
            <PhoneInput id="student-phone" value={value.student.phone} invalid={Boolean(fieldError("student-phone"))} onChange={(phone) => setStudent({ phone })} />
            <FieldError id="student-phone" message={fieldError("student-phone")} />
          </div>
          <TextArea id="student-address" label={text.address} required value={value.student.address} onChange={(address) => setStudent({ address })} autoComplete="street-address" error={fieldError("student-address")} />
          <Radios
            id="student-gender"
            name="student-gender"
            legend={text.gender}
            required
            value={value.student.gender}
            options={GENDERS.map((item) => ({ id: item.id, label: labelOf(item, locale) }))}
            onChange={(gender) => setStudent({ gender: gender as GenderId })}
            error={fieldError("student-gender")}
          />
          <p className="form-note">{story.funding}</p>
          <Checks
            id="student-race"
            legend={text.race}
            optional
            values={value.student.races}
            options={RACES.map((item) => ({ id: item.id, label: labelOf(item, locale) }))}
            onToggle={(id) => toggleRace(id as RaceId)}
          />
          <Radios
            id="student-school"
            name="student-school"
            legend={text.school}
            required
            value={value.student.school}
            options={SCHOOL_FORM_ORDER.map((school) => ({
              id: school,
              label: SCHOOL_FORM_LABELS[school],
            }))}
            onChange={(school) => setStudent({ school: school as School })}
            error={fieldError("student-school")}
          />

          <h3 className="reg-subhead">{text.parent}</h3>
          <div className="grid-2">
            <TextField id="guardian-first" label={text.first} required value={value.guardian.firstName} onChange={(firstName) => setGuardian({ firstName })} autoComplete="section-guardian given-name" error={fieldError("guardian-first")} />
            <TextField id="guardian-last" label={text.last} required value={value.guardian.lastName} onChange={(lastName) => setGuardian({ lastName })} autoComplete="section-guardian family-name" error={fieldError("guardian-last")} />
          </div>
          <div className="grid-2">
            <TextField id="guardian-email" label={text.email} required type="email" value={value.guardian.email} onChange={(email) => setGuardian({ email })} autoComplete="email" error={fieldError("guardian-email")} />
            <PhoneField id="guardian-phone" label={text.phone} required value={value.guardian.phone} onChange={(phone) => setGuardian({ phone })} error={fieldError("guardian-phone")} />
          </div>
          <Radios
            id="guardian-relationship"
            name="guardian-relationship"
            legend={text.relationship}
            required
            value={value.guardian.relationship}
            options={RELATIONSHIPS.map((item) => ({ id: item.id, label: labelOf(item, locale) }))}
            onChange={(relationship) => setGuardian({ relationship: relationship as RelationshipId })}
            error={fieldError("guardian-relationship")}
          />
          <TextArea id="guardian-address" label={text.guardianAddress} optional value={value.guardian.address} onChange={(address) => setGuardian({ address })} />
          <Checks
            id="guardian-help"
            legend={text.help}
            optional
            values={value.guardian.help}
            options={HELP_INTERESTS.map((item) => ({ id: item.id, label: labelOf(item, locale) }))}
            onToggle={(id) => toggleHelp(id as HelpInterestId)}
          />

          <h3 className="reg-subhead">{text.additional}</h3>
          <p className="form-note">{text.additionalHelp}</p>
          <div className="grid-2">
            <TextField id="additional-first" label={text.first} optional value={value.additionalGuardian.firstName} onChange={(firstName) => setAdditional({ firstName })} error={fieldError("additional-first")} />
            <TextField id="additional-last" label={text.last} optional value={value.additionalGuardian.lastName} onChange={(lastName) => setAdditional({ lastName })} />
          </div>
          <div className="grid-2">
            <TextField id="additional-email" label={text.email} optional type="email" value={value.additionalGuardian.email} onChange={(email) => setAdditional({ email })} error={fieldError("additional-email")} />
            <PhoneField id="additional-phone" label={text.phone} value={value.additionalGuardian.phone} onChange={(phone) => setAdditional({ phone })} error={fieldError("additional-phone")} optional />
          </div>
          <Radios
            id="additional-relationship"
            name="additional-relationship"
            legend={text.relationship}
            optional
            value={value.additionalGuardian.relationship}
            options={RELATIONSHIPS.map((item) => ({ id: item.id, label: labelOf(item, locale) }))}
            onChange={(relationship) => setAdditional({ relationship: relationship as RelationshipId })}
            error={fieldError("additional-relationship")}
          />
          <TextArea id="additional-address" label={text.additionalAddress} optional value={value.additionalGuardian.address} onChange={(address) => setAdditional({ address })} />
        </>
      ) : null}

      {step === "pickup" ? (
        <>
          <TextArea id="student-pickup" label={text.pickup} required value={value.student.pickupPeople} onChange={(pickupPeople) => setStudent({ pickupPeople })} error={fieldError("student-pickup")} />
          <Radios
            id="student-walk"
            name="student-walk"
            legend={text.walk}
            required
            layout="stack"
            value={value.student.walkHome}
            options={[
              { id: "yes", label: text.walkYes },
              { id: "no", label: text.walkNo },
            ]}
            onChange={(walkHome) => setStudent({ walkHome: walkHome as YesNo })}
            error={fieldError("student-walk")}
          />
        </>
      ) : null}

      {step === "health" ? (
        <>
          <EmergencySection
            idPrefix="emergency1"
            heading={text.emergency1}
            relationshipLabel={text.relationship}
            value={value.emergency1}
            text={text}
            fieldError={fieldError}
            onChange={(patch) => setEmergency("emergency1", patch)}
          />
          <EmergencySection
            idPrefix="emergency2"
            heading={text.emergency2}
            relationshipLabel={text.relationshipStudent}
            value={value.emergency2}
            text={text}
            fieldError={fieldError}
            onChange={(patch) => setEmergency("emergency2", patch)}
          />
          <Radios
            id="health-behavioral"
            name="health-behavioral"
            legend={text.behavioral}
            required
            layout="stack"
            value={value.health.behavioral}
            options={[
              { id: "yes", label: text.specifyYes },
              { id: "no", label: text.specifyNo },
            ]}
            onChange={(behavioral) => setHealth({ behavioral: behavioral as YesNo })}
            error={fieldError("health-behavioral")}
          />
          {value.health.behavioral === "yes" ? (
            <TextArea id="health-behavioral-detail" label={text.behavioralDetail} required value={value.health.behavioralDetail} onChange={(behavioralDetail) => setHealth({ behavioralDetail })} error={fieldError("health-behavioral-detail")} />
          ) : null}
          <Radios
            id="health-medical"
            name="health-medical"
            legend={text.medical}
            required
            layout="stack"
            value={value.health.medical}
            options={[
              { id: "yes", label: text.specifyYes },
              { id: "no", label: text.specifyNo },
            ]}
            onChange={(medical) => setHealth({ medical: medical as YesNo })}
            error={fieldError("health-medical")}
          />
          {value.health.medical === "yes" ? (
            <TextArea id="health-medical-detail" label={text.medicalDetail} required value={value.health.medicalDetail} onChange={(medicalDetail) => setHealth({ medicalDetail })} error={fieldError("health-medical-detail")} />
          ) : null}
          <Radios
            id="health-otc"
            name="health-otc"
            legend={text.otc}
            required
            layout="stack"
            value={value.health.otcMeds}
            options={[
              { id: "yes", label: text.authorizeYes },
              { id: "no", label: text.authorizeNo },
            ]}
            onChange={(otcMeds) => setHealth({ otcMeds: otcMeds as YesNo })}
            error={fieldError("health-otc")}
          />
          <Radios
            id="health-emergency-care"
            name="health-emergency-care"
            legend={text.emergencyCare}
            required
            layout="stack"
            value={value.health.emergencyCare}
            options={[
              { id: "yes", label: text.authorizeYes },
              { id: "no", label: text.authorizeNo },
            ]}
            onChange={(emergencyCare) => setHealth({ emergencyCare: emergencyCare as YesNo })}
            error={fieldError("health-emergency-care")}
          />
          <div className="grid-2">
            <TextField id="health-physician" label={text.physician} required value={value.health.physicianName} onChange={(physicianName) => setHealth({ physicianName })} error={fieldError("health-physician")} />
            <PhoneField id="health-physician-phone" label={text.physicianPhone} required value={value.health.physicianPhone} onChange={(physicianPhone) => setHealth({ physicianPhone })} error={fieldError("health-physician-phone")} />
          </div>
          <TextField id="health-hospital" label={text.hospital} optional value={value.health.hospital} onChange={(hospital) => setHealth({ hospital })} />
          <div className="grid-2">
            <TextField id="health-insurance" label={text.insurance} optional value={value.health.insuranceProvider} onChange={(insuranceProvider) => setHealth({ insuranceProvider })} />
            <TextField id="health-policy" label={text.policy} optional value={value.health.policyNumber} onChange={(policyNumber) => setHealth({ policyNumber })} />
          </div>
        </>
      ) : null}

      {step === "policies" ? (
        <div className="policy-list">
          <Agreement
            id="policy-conduct"
            name="policy-conduct"
            title={text.conductTitle}
            paragraphs={story.conduct}
            value={value.policies.conduct}
            agree={text.agree}
            meeting={text.meeting}
            responseLabel={text.responseLabel}
            onChange={(conduct) => setPolicies({ conduct })}
            error={fieldError("policy-conduct")}
          />
          <Agreement
            id="policy-behavior"
            name="policy-behavior"
            title={text.behaviorTitle}
            paragraphs={story.behavior}
            value={value.policies.behavior}
            agree={text.agree}
            meeting={text.meeting}
            responseLabel={text.responseLabel}
            onChange={(behavior) => setPolicies({ behavior })}
            error={fieldError("policy-behavior")}
          />
          <Agreement
            id="policy-dismissal"
            name="policy-dismissal"
            title={text.dismissalTitle}
            paragraphs={story.dismissal}
            value={value.policies.dismissal}
            agree={text.agree}
            meeting={text.meeting}
            responseLabel={text.responseLabel}
            onChange={(dismissal) => setPolicies({ dismissal })}
            error={fieldError("policy-dismissal")}
          />
          <PolicyCard
            id="policy-media"
            title={text.mediaTitle}
            paragraphs={[story.media]}
            legal
            error={fieldError("policy-media")}
          >
            <p className="policy-ack__label">{text.responseLabel}</p>
            <div className="policy-choices policy-choices--even">
              <Choice name="policy-media" id="policy-media-yes" label={text.mediaYes} checked={value.policies.media === "yes"} onChange={() => setPolicies({ media: "yes" })} />
              <Choice name="policy-media" id="policy-media-no" label={text.mediaNo} checked={value.policies.media === "no"} onChange={() => setPolicies({ media: "no" })} />
            </div>
            <FieldError id="policy-media" message={fieldError("policy-media")} />
          </PolicyCard>
          <Liability id="liability-participate" title={text.participateTitle} checked={value.liability.participate} label={text.iAgree} ackLabel={text.ackLabel} paragraphs={story.participate} onChange={(participate) => setLiability({ participate })} error={fieldError("liability-participate")} />
          <Liability id="liability-transport" title={text.transportTitle} checked={value.liability.transport} label={text.iAgree} ackLabel={text.ackLabel} paragraphs={story.transport} onChange={(transport) => setLiability({ transport })} error={fieldError("liability-transport")} />
          <Liability id="liability-risks" title={text.risksTitle} checked={value.liability.risks} label={text.iAgree} ackLabel={text.ackLabel} paragraphs={story.risks} onChange={(risks) => setLiability({ risks })} error={fieldError("liability-risks")} />
          <Liability id="liability-release" title={text.releaseTitle} checked={value.liability.release} label={text.iAgree} ackLabel={text.ackLabel} paragraphs={story.release} onChange={(release) => setLiability({ release })} error={fieldError("liability-release")} />
        </div>
      ) : null}

      {step === "review" ? (
        <Review
          value={value}
          locale={locale}
          text={text}
          editLabel={chrome[locale].edit}
          onEditStep={onEditStep}
        />
      ) : null}
    </div>
  );
}

function labelOf(item: { en: string; es: string }, locale: Locale): string {
  return locale === "es" ? item.es : item.en;
}

function FieldError({ id, message }: { id: string; message: string }) {
  if (!message) return null;
  return (
    <p className="field-error" id={`${id}-error`}>
      {message}
    </p>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  required,
  optional,
  type = "text",
  autoComplete,
  error = "",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  optional?: boolean;
  type?: string;
  autoComplete?: string;
  error?: string;
}) {
  return (
    <div className={`field${error ? " field--invalid" : ""}`} id={`${id}-field`}>
      <FormFieldLabel htmlFor={id} required={required} optional={optional} presentation="asterisk">
        {label}
      </FormFieldLabel>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError id={id} message={error} />
    </div>
  );
}

function PhoneField({
  id,
  label,
  value,
  onChange,
  required,
  optional,
  error = "",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  optional?: boolean;
  error?: string;
}) {
  return (
    <div className={`field${error ? " field--invalid" : ""}`} id={`${id}-field`}>
      <FormFieldLabel htmlFor={id} required={required} optional={optional} presentation="asterisk">
        {label}
      </FormFieldLabel>
      <PhoneInput id={id} value={value} onChange={onChange} invalid={Boolean(error)} />
      <FieldError id={id} message={error} />
    </div>
  );
}

function TextArea({
  id,
  label,
  value,
  onChange,
  required,
  optional,
  autoComplete,
  error = "",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  optional?: boolean;
  autoComplete?: string;
  error?: string;
}) {
  return (
    <div className={`field${error ? " field--invalid" : ""}`}>
      <FormFieldLabel htmlFor={id} required={required} optional={optional} presentation="asterisk">
        {label}
      </FormFieldLabel>
      <textarea
        id={id}
        rows={3}
        value={value}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError id={id} message={error} />
    </div>
  );
}

function Radios({
  id,
  name,
  legend,
  value,
  options,
  onChange,
  required,
  optional,
  layout = "cards",
  error = "",
}: {
  id: string;
  name: string;
  legend: string;
  value: string;
  options: Array<{ id: string; label: string }>;
  onChange: (value: string) => void;
  required?: boolean;
  optional?: boolean;
  layout?: "cards" | "stack";
  error?: string;
}) {
  return (
    <fieldset className={`field${error ? " field--invalid" : ""}`} id={id} aria-describedby={error ? `${id}-error` : undefined}>
      <legend>
        {legend}
        {required ? <Mark required /> : null}
        {optional ? <Mark optional /> : null}
      </legend>
      <div className={layout === "stack" ? "choice-cards choice-cards--stack" : "choice-cards"}>
        {options.map((option) => (
          <Choice
            key={option.id}
            name={name}
            id={`${id}-${option.id}`}
            label={option.label}
            checked={value === option.id}
            onChange={() => onChange(option.id)}
          />
        ))}
      </div>
      <FieldError id={id} message={error} />
    </fieldset>
  );
}

function Checks({
  id,
  legend,
  values,
  options,
  onToggle,
  optional,
}: {
  id: string;
  legend: string;
  values: readonly string[];
  options: Array<{ id: string; label: string }>;
  onToggle: (id: string) => void;
  optional?: boolean;
}) {
  return (
    <fieldset className="field" id={id}>
      <legend>
        {legend}
        {optional ? <Mark optional /> : null}
      </legend>
      <div className="choice-cards">
        {options.map((option) => (
          <label key={option.id} className={`choice-card${values.includes(option.id) ? " is-selected" : ""}`}>
            <input
              type="checkbox"
              checked={values.includes(option.id)}
              onChange={() => onToggle(option.id)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Choice({
  name,
  id,
  label,
  checked,
  onChange,
}: {
  name: string;
  id: string;
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className={`choice-card${checked ? " is-selected" : ""}`}>
      <input id={id} type="radio" name={name} checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

function Mark({ required, optional }: { required?: boolean; optional?: boolean }) {
  const { t } = useLocale();
  if (required) {
    return (
      <>
        <abbr className="field-mark field-mark--star" title={t("register.required")}>
          *
        </abbr>
        <span className="sr-only"> {t("register.required")}</span>
      </>
    );
  }
  if (optional) {
    return <span className="field-mark field-mark--optional"> {t("register.optional")}</span>;
  }
  return null;
}

function EmergencySection({
  idPrefix,
  heading,
  relationshipLabel,
  value,
  text,
  fieldError,
  onChange,
}: {
  idPrefix: string;
  heading: string;
  relationshipLabel: string;
  value: PermissionAnswers["emergency1"];
  text: (typeof ui)[Locale];
  fieldError: (id: string) => string;
  onChange: (patch: Partial<PermissionAnswers["emergency1"]>) => void;
}) {
  return (
    <section className="reg-subsection" aria-labelledby={`${idPrefix}-heading`}>
      <h3 id={`${idPrefix}-heading`} className="reg-subhead">
        {heading}
      </h3>
      <div className="grid-2">
        <TextField id={`${idPrefix}-first`} label={text.first} required value={value.firstName} onChange={(firstName) => onChange({ firstName })} error={fieldError(`${idPrefix}-first`)} />
        <TextField id={`${idPrefix}-last`} label={text.last} required value={value.lastName} onChange={(lastName) => onChange({ lastName })} error={fieldError(`${idPrefix}-last`)} />
      </div>
      <div className="grid-2">
        <PhoneField id={`${idPrefix}-phone`} label={text.phone} required value={value.phone} onChange={(phone) => onChange({ phone })} error={fieldError(`${idPrefix}-phone`)} />
        <TextField id={`${idPrefix}-relationship`} label={relationshipLabel} required value={value.relationship} onChange={(relationship) => onChange({ relationship })} error={fieldError(`${idPrefix}-relationship`)} />
      </div>
    </section>
  );
}

function PolicyCard({
  id,
  title,
  paragraphs,
  legal = false,
  error = "",
  children,
}: {
  id: string;
  title: string;
  paragraphs: readonly string[];
  legal?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <fieldset className={`policy-card field${error ? " field--invalid" : ""}`} id={id} aria-labelledby={`${id}-title`} aria-describedby={error ? `${id}-error` : undefined}>
      <h3 id={`${id}-title`}>{title}</h3>
      <div className={legal ? "policy-legal" : "policy-prose"}>
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      <div className="policy-ack">{children}</div>
    </fieldset>
  );
}

function Agreement({
  id,
  name,
  title,
  paragraphs,
  value,
  agree,
  meeting,
  responseLabel,
  onChange,
  error = "",
}: {
  id: string;
  name: string;
  title: string;
  paragraphs: readonly string[];
  value: AgreeChoice | "";
  agree: string;
  meeting: string;
  responseLabel: string;
  onChange: (value: AgreeChoice) => void;
  error?: string;
}) {
  return (
    <PolicyCard id={id} title={title} paragraphs={paragraphs} error={error}>
      <p className="policy-ack__label">{responseLabel}</p>
      <div className="policy-choices policy-choices--pair">
        <Choice name={name} id={`${id}-agree`} label={agree} checked={value === "agree"} onChange={() => onChange("agree")} />
        <Choice name={name} id={`${id}-meeting`} label={meeting} checked={value === "meeting"} onChange={() => onChange("meeting")} />
      </div>
      <FieldError id={id} message={error} />
    </PolicyCard>
  );
}

function Liability({
  id,
  title,
  paragraphs,
  checked,
  label,
  ackLabel,
  onChange,
  error = "",
}: {
  id: string;
  title: string;
  paragraphs: readonly string[];
  checked: boolean;
  label: string;
  ackLabel: string;
  onChange: (value: boolean) => void;
  error?: string;
}) {
  return (
    <PolicyCard id={id} title={title} paragraphs={paragraphs} legal error={error}>
      <p className="policy-ack__label">{ackLabel}</p>
      <label className={`choice-card choice-card--check${checked ? " is-selected" : ""}`}>
        <input
          id={`${id}-check`}
          type="checkbox"
          checked={checked}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{label}</span>
      </label>
      <FieldError id={id} message={error} />
    </PolicyCard>
  );
}

function Review({
  value,
  locale,
  text,
  editLabel,
  onEditStep,
}: {
  value: PermissionAnswers;
  locale: Locale;
  text: (typeof ui)[Locale];
  editLabel: string;
  onEditStep?: (step: RegistrationStep) => void;
}) {
  const option = (list: ReadonlyArray<{ id: string; en: string; es: string }>, id: string) => {
    const found = list.find((item) => item.id === id);
    return found ? labelOf(found, locale) : "";
  };
  const yesNo = (item: YesNo | "", yes: string, no: string) =>
    item === "yes" ? yes : item === "no" ? no : "";
  const agree = (item: AgreeChoice | "") =>
    item === "agree" ? text.agree : item === "meeting" ? text.meeting : "";

  const studentRows: ReviewRow[] = [
    [text.first, value.student.firstName],
    [text.last, value.student.lastName],
    [text.email, value.student.email],
    [text.grade, value.student.grade === "" ? "" : gradeLabel(value.student.grade)],
    [text.birthday, value.student.birthday ? formatBirthdayLabel(value.student.birthday) : ""],
    [text.studentPhone, value.student.phone],
    [text.address, value.student.address],
    [text.gender, option(GENDERS, value.student.gender)],
    [text.race, value.student.races.map((id) => option(RACES, id)).filter(Boolean).join(", ")],
    [text.school, value.student.school ? SCHOOL_FORM_LABELS[value.student.school] : ""],
    [text.parent, `${value.guardian.firstName} ${value.guardian.lastName}`.trim()],
    [text.email, value.guardian.email],
    [text.phone, value.guardian.phone],
    [text.relationship, option(RELATIONSHIPS, value.guardian.relationship)],
    [text.guardianAddress, value.guardian.address],
    [text.help, value.guardian.help.map((id) => option(HELP_INTERESTS, id)).filter(Boolean).join(", ")],
  ];
  const extraName = `${value.additionalGuardian.firstName} ${value.additionalGuardian.lastName}`.trim();
  if (
    extraName ||
    value.additionalGuardian.email ||
    value.additionalGuardian.phone ||
    value.additionalGuardian.relationship ||
    value.additionalGuardian.address
  ) {
    studentRows.push(
      [text.additional, extraName],
      [text.email, value.additionalGuardian.email],
      [text.phone, value.additionalGuardian.phone],
      [text.relationship, option(RELATIONSHIPS, value.additionalGuardian.relationship)],
      [text.additionalAddress, value.additionalGuardian.address],
    );
  }

  const pickupRows: ReviewRow[] = [
    [text.pickup, value.student.pickupPeople],
    [text.walk, yesNo(value.student.walkHome, text.walkYes, text.walkNo)],
  ];

  const healthRows: ReviewRow[] = [
    [text.emergency1, `${value.emergency1.firstName} ${value.emergency1.lastName}`.trim()],
    [text.phone, value.emergency1.phone],
    [text.relationship, value.emergency1.relationship],
    [text.emergency2, `${value.emergency2.firstName} ${value.emergency2.lastName}`.trim()],
    [text.phone, value.emergency2.phone],
    [text.relationshipStudent, value.emergency2.relationship],
    [text.behavioralDetail, yesNo(value.health.behavioral, value.health.behavioralDetail || text.specifyYes, text.specifyNo)],
    [text.medicalDetail, yesNo(value.health.medical, value.health.medicalDetail || text.specifyYes, text.specifyNo)],
    [text.otc, yesNo(value.health.otcMeds, text.authorizeYes, text.authorizeNo)],
    [text.emergencyCare, yesNo(value.health.emergencyCare, text.authorizeYes, text.authorizeNo)],
    [text.physician, value.health.physicianName],
    [text.physicianPhone, value.health.physicianPhone],
    [text.hospital, value.health.hospital],
    [text.insurance, value.health.insuranceProvider],
    [text.policy, value.health.policyNumber],
  ];

  const policyRows: ReviewRow[] = [
    [text.conductTitle, agree(value.policies.conduct)],
    [text.behaviorTitle, agree(value.policies.behavior)],
    [text.dismissalTitle, agree(value.policies.dismissal)],
    [text.mediaTitle, yesNo(value.policies.media, text.mediaYes, text.mediaNo)],
    [text.participateTitle, value.liability.participate ? text.iAgree : text.notAnswered],
    [text.transportTitle, value.liability.transport ? text.iAgree : text.notAnswered],
    [text.risksTitle, value.liability.risks ? text.iAgree : text.notAnswered],
    [text.releaseTitle, value.liability.release ? text.iAgree : text.notAnswered],
  ];

  return (
    <div className="review-list">
      <ReviewBlock title={text.studentTitle} rows={studentRows} editLabel={editLabel} onEdit={onEditStep ? () => onEditStep("student") : undefined} />
      <ReviewBlock title={text.pickupTitle} rows={pickupRows} editLabel={editLabel} onEdit={onEditStep ? () => onEditStep("pickup") : undefined} />
      <ReviewBlock title={text.healthTitle} rows={healthRows} editLabel={editLabel} onEdit={onEditStep ? () => onEditStep("health") : undefined} />
      <ReviewBlock title={text.policiesTitle} rows={policyRows} editLabel={editLabel} onEdit={onEditStep ? () => onEditStep("policies") : undefined} />
    </div>
  );
}

type ReviewRow = [string, string];

function ReviewBlock({
  title,
  rows,
  editLabel,
  onEdit,
}: {
  title: string;
  rows: ReviewRow[];
  editLabel: string;
  onEdit?: () => void;
}) {
  const visible = rows.filter(([, item]) => item.trim());
  return (
    <section className="review-block">
      <header>
        <h3>{title}</h3>
        {onEdit ? (
          <button type="button" className="reg-text" onClick={onEdit}>
            {editLabel}
          </button>
        ) : null}
      </header>
      <dl>
        {visible.map(([label, item], index) => (
          <div key={`${label}-${index}`}>
            <dt>{label}</dt>
            <dd>{item}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
